import { useEffect, useState } from 'react'
import { safeText } from '../../lib/serverText.js'
import { api } from '../../lib/api.js'
import { tamView, tamAge } from '../../lib/tamChain.js'
import {
  opportunityValue, valueText, money,
} from './worklist/opportunityValue.js'
import styles from './TamChainPanel.module.css'

/**
 * THE MATH, ON THE RECORD. Brian: "Where is the pipeline value and the math
 * behind it."
 *
 * So this renders the FACTORS, in the order they multiply, each with the thing
 * that makes it arguable — a published bracket or an archetype, a mean with its
 * n, an assumption, a forward price with the history it departs from. The
 * product is the last line, not the first.
 *
 * ⚠️ NOTHING HERE IS A HEADLINE. A ceiling beside a rep's name reads as their
 * number, so `headlineEligible` is honoured by lib/tamChain.js and the ceiling
 * always renders second and labelled. Every guard lives in that module, which is
 * pure and has 18 assertions in verify-tam-chain.mjs; this file only draws.
 */
export default function TamChainPanel({ recordId }) {
  const [state, setState] = useState({ status: 'loading', view: null, error: null })

  useEffect(() => {
    if (!recordId) return undefined
    let cancelled = false
    const ctrl = new AbortController()
    setState({ status: 'loading', view: null, error: null })
    api
      .tamRecord(recordId, { signal: ctrl.signal })
      /* ⚠️ THE RAW RESPONSE IS KEPT ALONGSIDE THE VIEW, and that is the whole
       * wiring for the per-opportunity figure: it is computed from the SAME
       * fetch, so the honest small number costs no extra request. One record
       * open, one call — which is exactly what `/api/tam/record/:id` supports.
       * (Ranking a whole book by dollars is what it does NOT support; see
       * docs/REVENUE-RANKING-2026-10-04.md.) */
      .then((res) => { if (!cancelled) setState({ status: 'done', view: tamView(res), chain: res, error: null }) })
      .catch((err) => {
        if (cancelled) return
        /* ⚠️ A 403 FROM THIS ROUTE CARRIES THE EXPLANATION IN ITS BODY. The API
         * answers a refusal with `render: "error-not-empty"` and a `detail`
         * written for the reader, so the body is preferred over the thrown
         * message — and a transport failure that has no body is reported as a
         * transport failure rather than as a refusal. */
        const body = err?.body || err?.payload || null
        if (body && (body.detail || body.render)) {
          setState({ status: 'done', view: tamView(body), error: null })
        } else {
          setState({ status: 'done', view: null, error: String(err?.message || err) })
        }
      })
    return () => { cancelled = true; ctrl.abort() }
  }, [recordId])

  if (state.status === 'loading') {
    return (
      <div className={styles.panel}>
        <div className="eyebrow">Pipeline value</div>
        <p className={styles.dim}><span className="spinner" /> Working out the math…</p>
      </div>
    )
  }

  /* A transport failure. Not a zero, and not a refusal. */
  if (state.error) {
    return (
      <div className={styles.panel} data-kind="error" role="alert">
        <div className="eyebrow">Pipeline value</div>
        <p className={styles.detail}>
          <strong>Could not load the math.</strong> This did not load, so there is no figure to
          show. This does not mean zero. {state.error}
        </p>
      </div>
    )
  }

  if (!state.view) return null
  /* The per-opportunity figure is computed HERE, in the container that owns the
   * fetch, and handed to the view as a finished answer — see the note on
   * `TamChainView`. `state.chain` is the raw response from the SAME call, so
   * this costs no extra request. */
  return <TamChainView view={state.view} value={state.chain ? opportunityValue(state.chain) : null} />
}

/**
 * THE PRESENTATION, SEPARATED FROM THE FETCH — exported so it can be rendered
 * from a view model with no network and no session.
 *
 * ⚠️ WHY THE SPLIT EXISTS. The panel fetches, and the shared request helper
 * refuses before it ever reaches the network when there is no signed-in session
 * — so in a dev harness the whole component could only ever render its error
 * state. The one
 * surface built to show Brian the MATH could not be shown. Splitting the drawing
 * from the fetching means a fixture renders the real shipped markup, and nothing
 * has to simulate a credential to look at it.
 *
 * It takes a `tamView()` result, so every guard still lives in lib/tamChain.js
 * and this function cannot accidentally become a second place that decides what
 * a figure means.
 */
/* ⚠️ `value` IS A PROP, NOT COMPUTED IN HERE, AND THE COMMENT ABOVE IS WHY.
 * My first attempt called `opportunityValue(state.chain)` inside this function —
 * which ESLint refused (`'state' is not defined`) and which would have been
 * wrong even if it had resolved: this file says out loud that the view "takes a
 * tamView() result, so every guard still lives in lib/tamChain.js and this
 * function cannot accidentally become a second place that decides what a figure
 * means." Reaching into raw response data from the renderer is exactly that.
 * The container computes it; this draws it. The gate caught the scope error and
 * the design note caught the design error. */
export function TamChainView({ view: v, value = null }) {
  if (!v) return null

  /* THE REFUSAL, VERBATIM. The server wrote this sentence for the person
   * reading it; paraphrasing it would lose the one thing it is for. */
  if (v.kind === 'denied') {
    return (
      <div className={styles.panel} data-kind="error" role="alert">
        <div className="eyebrow">Pipeline value</div>
        <p className={styles.detail}>{v.detail}</p>
        {v.error && <p className={styles.dim}>Reported as <code>{v.error}</code>.</p>}
        {v.notPay && <p className={styles.notPay}>{v.notPay}</p>}
        {v.provenance && <p className={styles.dim}>{tamAge(v.provenance)}</p>}
      </div>
    )
  }

  return (
    <div className={styles.panel} aria-label="Pipeline value and assumptions">
      <div className="eyebrow">Pipeline value and assumptions</div>

      {/* The measured figure leads, or nothing does. */}
      {v.headline
        ? (
          <div className={styles.headline}>
            <div className={styles.headlineNum}>{v.headline.text}</div>
            <div className={styles.headlineLabel}>{v.headline.label}</div>
            {v.headline.reason && <div className={styles.dim}>{safeText(v.headline.reason)}</div>}
          </div>
        )
        : (
          <p className={styles.detail}>
            <strong>No measured figure for this record.</strong> Any market-size ceiling below is a theoretical maximum, not expected revenue.
          </p>
        )}

      {/* ⭐ THE NUMBER A REP CAN ACTUALLY QUOTE, ABOVE THE MARKET-SIZING ONE.
        *
        * Brian: *"takes the math we discussed on average price per unit, looks
        * at the sport and puts a dollar figure to it."* The chain below this is
        * the TAM — fieldSize × units × penetration × price — and at 100%
        * penetration it is a CEILING, roughly 11× realised revenue. ⚠️ A rep
        * would never say it to an athletic director, and it was the figure the
        * worklist was first pointed at. This is the other one: units for THIS
        * sport and level × the price per unit, which on Brian's 2026-10-04
        * figures for HS football lands in the tens of thousands rather than
        * nine — the worked arithmetic lives in opportunityValue.js, dated, and
        * is not repeated here.
        *
        * ⚠️ IT IS FIRST ON PURPOSE. The ceiling has every caveat it needs and
        * keeps them; what it must not be is the first dollar figure a person
        * reads on a single record, because the first number is the one that
        * gets repeated. Computed from the SAME fetch — no extra request. */}
      {value && (
        <div className={styles.opportunity} data-confidence={value.confidence.key}>
          <div className="eyebrow">Estimated value of this opportunity</div>
          {value.usd != null ? (
            <>
              <p className={styles.oppFigure}>{valueText(value)}</p>
              <p className={styles.oppBasis}>
                {safeText(value.basis).replace(/ — /g, '; ')}
                {' · '}
                <span className={styles.oppConfidence}>{safeText(value.confidence.label).replace(/ — /g, '; ')}</span>
              </p>
              {value.low != null && value.high != null && (
                <p className={styles.oppMid}>Midpoint {money(value.usd)}.</p>
              )}
            </>
          ) : (
            /* ⚠️ NEVER A BARE ZERO. A zero would say this championship is worth
               nothing — a claim about the opportunity. The null says we have not
               measured this sport, which is a claim about our data, and a rep
               acts differently on each. */
            /* ⚠️ THE SERVER'S WORDS, BUT ONLY IF THEY ARE WORDS. This is the
               line that put `{"d":270.16,"allDelivered":223.71}` on Brian's
               phone — the reason field arrived carrying a serialized object
               and this rendered it faithfully. `safeText` keeps real prose
               verbatim and replaces a machine-shaped payload with a sentence
               that names the right system. */
            <p className={styles.oppGap}>
              <b>No figure for this one yet.</b> {safeText(value.reason).replace(/ — /g, '; ')}
            </p>
          )}
        </div>
      )}

      {v.formula && <p className={styles.formula}>{v.formula}</p>}

      {/* ⭐ THE FACTORS. The point of the whole panel. */}
      {v.factors.length > 0 && (
        <table className={styles.factors}>
          <tbody>
            {v.factors.map((f) => (
              /* Declared a readout: a factor and its value in the TAM chain —
                 label, number, provenance. There is nothing to open. */
              <tr key={f.key} data-weak={f.weak || undefined} aria-disabled="true">
                <th scope="row">{f.label}</th>
                <td className={styles.factorValue}>
                  {f.value == null ? <span className={styles.dim}>not reported</span> : f.value}
                </td>
                <td className={styles.factorWhy}>
                  <span className={f.weak ? styles.weak : undefined}>{safeText(f.qualifier)}</span>
                  {f.caveat && <div className={styles.dim}>{safeText(f.caveat)}</div>}
                  {f.distribution && (
                    <div className={styles.weak}>{safeText(f.distribution.headline)}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {v.product && (
        <p className={styles.detail}>
          Calculated total: <strong>{v.product.text}</strong>
          {v.product.reason && <span className={styles.dim}> · {safeText(v.product.reason)}</span>}
        </p>
      )}

      {/* The ceiling, second and labelled, every time. */}
      {v.ceiling && (
        <p className={styles.ceiling}>
          <strong>{v.ceiling.text}</strong> {v.ceiling.label}
          {v.ceiling.reason && <span className={styles.dim}> · {safeText(v.ceiling.reason)}</span>}
          {!v.ceiling.headlineEligible && (
            <span className={styles.dim}> Not expected revenue.</span>
          )}
        </p>
      )}

      {v.records != null && (
        <p className={styles.dim}>
          Across {v.records.toLocaleString('en-US')} record{v.records === 1 ? '' : 's'}
          {v.recordsReason ? ` · ${safeText(v.recordsReason)}` : ''}
        </p>
      )}
      {v.notPay && <p className={styles.notPay}>{v.notPay}</p>}
      {v.provenance && <p className={styles.dim}>{tamAge(v.provenance)}</p>}
    </div>
  )
}
